import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Bloqueia edits no trecho de PREFIX de um `<input>`, mantendo a seleção
 * (highlight/copy) livre em qualquer parte do valor — incluindo o prefix.
 *
 * Caso de uso: campo de URL onde a parte editável é só o slug (ex.:
 *   "https://jems3.sbc.org.br/" + "sbrc2025"
 * Queremos que o usuário possa SELECIONAR a URL inteira nativamente pra
 * copiar, mas só consiga EDITAR o slug após a "/".
 *
 * Mecânica:
 *  • `beforeinput` (cancela): qualquer mutação cujo range tocaria o trecho
 *    do prefix (`selectionStart < prefixLen` OU `selectionEnd <= prefixLen`)
 *    é abortada. O caret é empurrado pra logo após o prefix.
 *  • `keydown` Home/Cmd+Left: redireciona o caret pra início do slug em
 *    vez de início absoluto (preserva o pulo de "início" semanticamente).
 *  • `select`/click: não interferimos — usuário seleciona o que quiser.
 *  • `paste`/`drop`: o `beforeinput` cobre.
 *
 * Uso:
 *   <input [value]="fullUrl" [appFixedPrefix]="baseUrl + '/'">
 *
 * O form control deve armazenar a URL INTEIRA (com prefix). Consumidores
 * extraem o slug via `value.slice(prefix.length)` no save/load.
 */
@Directive({
  selector: '[appFixedPrefix]'
})
export class FixedPrefixDirective implements AfterViewInit, OnChanges {
  @Input() appFixedPrefix = '';

  /** Último valor "bom" (que começa com prefix). Snapshot pra rollback
   *  caso o usuário consiga editar o prefix por algum caminho que o
   *  beforeinput não cubra (drag&drop, IME, autofill etc.). */
  private lastGood = '';
  private lastCursor = 0;

  constructor(private el: ElementRef<HTMLInputElement>) {}

  private get prefixLen(): number { return (this.appFixedPrefix || '').length; }

  ngAfterViewInit(): void { this.snapshot(); }
  ngOnChanges(_c: SimpleChanges): void { this.snapshot(); }

  private snapshot(): void {
    const input = this.el.nativeElement;
    if (this.prefixLen && input.value.startsWith(this.appFixedPrefix)) {
      this.lastGood = input.value;
      this.lastCursor = input.selectionStart ?? input.value.length;
    }
  }

  /** Rollback se a edição saiu da regra (prefix removido/modificado).
   *  Captura o valor "bom" preguiçosamente: na primeira vez que o input
   *  contiver o prefix, vira snapshot — assim contornamos a ordem das
   *  fases de init (formControl pode patchar APÓS o ngAfterViewInit). */
  @HostListener('input')
  onInput(): void {
    if (!this.prefixLen) { return; }
    const input = this.el.nativeElement;
    if (input.value.startsWith(this.appFixedPrefix)) {
      this.lastGood = input.value;
      this.lastCursor = input.selectionStart ?? input.value.length;
      return;
    }
    // Edit quebrou o prefix — restaura. Se nunca tivemos snapshot, usa
    // só o prefix (slug vazio).
    const restore = this.lastGood || this.appFixedPrefix;
    const cursor = this.lastGood ? this.lastCursor : this.prefixLen;
    input.value = restore;
    input.setSelectionRange(cursor, cursor);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.prefixLen) { return; }
    const input = this.el.nativeElement;
    if (event.key === 'Home' && !event.shiftKey) {
      event.preventDefault();
      input.setSelectionRange(this.prefixLen, this.prefixLen);
    }
  }

  /** Ao focar, empurra o caret pro início do slug (caso default pouse no
   *  prefix) — assim a primeira digitação não cai no rollback. */
  @HostListener('focus')
  onFocus(): void {
    if (!this.prefixLen) { return; }
    const input = this.el.nativeElement;
    Promise.resolve().then(() => {
      const pos = input.selectionStart ?? 0;
      if (pos < this.prefixLen) {
        input.setSelectionRange(this.prefixLen, input.value.length);
      }
    });
  }
}
